import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Brand";
import Cta from "@/components/ui-kit/Cta";
import { NAV_LINKS, LIVE_APP_URL } from "@/lib/site";
import { trackCta } from "@/lib/analytics";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const overlayRef = useRef(null);
  const firstLinkRef = useRef(null);
  const isHome = location.pathname === "/";
  const overDark = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Body scroll lock + focus management for the overlay
  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstLinkRef.current?.focus(), 40);

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "Tab" && overlayRef.current) {
        const focusables = overlayRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-semibold transition-colors duration-200 ${
      overDark
        ? isActive
          ? "text-etap-mint-200"
          : "text-white/85 hover:text-white"
        : isActive
          ? "text-etap-primary"
          : "text-etap-deep-700 hover:text-etap-primary"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/85 shadow-etap-sm backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <div className={`flex items-center transition-all duration-300 ${scrolled ? "h-20" : "h-24"}`}>
          <Logo testId="nav-logo" variant={overDark ? "white" : "color"} />
        </div>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.to.replace("/", "")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            href={LIVE_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta("nav-login")}
            className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors ${
              overDark ? "text-white/90 hover:text-etap-mint-200" : "text-etap-deep-700 hover:text-etap-primary"
            }`}
            data-testid="nav-login"
          >
            Log in
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <Cta to="/get-started" variant="primary" location="nav" testId="nav-get-started">
            Get Started
          </Cta>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-etap-deep-100 bg-white text-etap-deep-700 lg:hidden"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
          data-testid="mobile-menu-open"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile full-screen overlay */}
      {open && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          data-testid="mobile-menu"
        >
          <div className="flex h-20 items-center justify-between px-5">
            <Logo onClick={() => setOpen(false)} />
            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-etap-deep-100 text-etap-deep-700"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col gap-1 px-5 pt-4" aria-label="Mobile">
            {NAV_LINKS.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                ref={i === 0 ? firstLinkRef : undefined}
                className={({ isActive }) =>
                  `border-b border-etap-deep-50 py-4 font-display text-xl font-semibold ${
                    isActive ? "text-etap-primary" : "text-etap-deep-800"
                  }`
                }
                data-testid={`mobile-nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex flex-col gap-3 px-5 pb-10">
            <Cta to="/get-started" variant="primary" size="lg" location="mobile-nav" testId="mobile-get-started">
              Get Started
            </Cta>
            <Cta href={LIVE_APP_URL} variant="outline" size="lg" location="mobile-login" testId="mobile-login">
              Log in
            </Cta>
          </div>
        </div>
      )}
    </header>
  );
}
